import { apiClient } from './api'
import {
  DashboardStats,
  Transaction,
  SaleShop,
  Customer,
  Currency,
  Country,
  PaymentLink,
  CreatePaymentLinkData,
  UpdatePaymentLinkData,
} from '@/types'

class DashboardService {
  // Get dashboard statistics
  async getDashboardStats(): Promise<DashboardStats> {
    return apiClient.get<DashboardStats>('/dashboard/stats/')
  }

  // Get recent transactions
  async getRecentTransactions(limit = 10): Promise<Transaction[]> {
    return apiClient.get<Transaction[]>(`/transactions/recent/?limit=${limit}`)
  }

  // Get all transactions with pagination
  async getTransactions(params?: {
    page?: number
    limit?: number
    status?: string
    sale_shop?: number
    start_date?: string
    end_date?: string
  }): Promise<{
    success: boolean
    message: string
    data: {
      count: number
      next: string | null
      previous: string | null
      results: Transaction[]
    }
  }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.sale_shop) queryParams.append('sale_shop', params.sale_shop.toString())
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)

    const queryString = queryParams.toString()
    const url = queryString ? `/transactions/?${queryString}` : '/transactions/'

    return apiClient.get<{
      success: boolean
      message: string
      data: {
        count: number
        next: string | null
        previous: string | null
        results: Transaction[]
      }
    }>(url)
  }

  // Get sale shops
  async getSaleShops(): Promise<SaleShop[]> {
    return apiClient.get<SaleShop[]>('/sale-shops/')
  }

  // Get customers
  async getCustomers(params?: { page?: number; limit?: number; search?: string }): Promise<{
    count: number
    next: string | null
    previous: string | null
    results: Customer[]
  }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)

    const queryString = queryParams.toString()
    const url = queryString ? `/customers/?${queryString}` : '/customers/'

    return apiClient.get<{
      count: number
      next: string | null
      previous: string | null
      results: Customer[]
    }>(url)
  }

  // Get currencies
  async getCurrencies(): Promise<Currency[]> {
    return apiClient.get<Currency[]>('/currencies/')
  }

  // Get countries
  async getCountries(): Promise<Country[]> {
    return apiClient.get<Country[]>('/countries/')
  }

  // Get transaction by ID
  async getTransaction(transactionId: number): Promise<Transaction> {
    return apiClient.get<Transaction>(`/transactions/${transactionId}/`)
  }

  // Get sale shop by ID
  async getSaleShop(saleShopId: number): Promise<SaleShop> {
    return apiClient.get<SaleShop>(`/sale-shops/${saleShopId}/`)
  }

  // Get customer by ID
  async getCustomer(customerId: number): Promise<Customer> {
    return apiClient.get<Customer>(`/customers/${customerId}/`)
  }

  // Create sale shop
  async createSaleShop(data: {
    name: string
    description?: string
    website_url?: string
    webhook_url?: string
    redirect_url?: string
    cancel_url?: string
    currency?: number
  }): Promise<SaleShop> {
    return apiClient.post<SaleShop>('/sale-shops/', data)
  }

  // Update sale shop
  async updateSaleShop(saleShopId: number, data: Partial<SaleShop>): Promise<SaleShop> {
    return apiClient.patch<SaleShop>(`/sale-shops/${saleShopId}/`, data)
  }

  // Delete sale shop
  async deleteSaleShop(saleShopId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`/sale-shops/${saleShopId}/`)
  }

  // Get transaction statistics
  async getTransactionStats(params?: {
    start_date?: string
    end_date?: string
    sale_shop?: number
  }): Promise<{
    total_transactions: number
    total_revenue: string
    pending_transactions: number
    completed_transactions: number
    failed_transactions: number
    average_transaction_value: string
  }> {
    const queryParams = new URLSearchParams()

    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.sale_shop) queryParams.append('sale_shop', params.sale_shop.toString())

    const queryString = queryParams.toString()
    const url = queryString ? `/transactions/stats/?${queryString}` : '/transactions/stats/'

    return apiClient.get<{
      total_transactions: number
      total_revenue: string
      pending_transactions: number
      completed_transactions: number
      failed_transactions: number
      average_transaction_value: string
    }>(url)
  }

  // Get payment links
  async getPaymentLinks(params?: {
    page?: number
    limit?: number
    search?: string
    sale_shop?: number
    is_active?: boolean
  }): Promise<{
    success: boolean
    message: string
    data: {
      count: number
      next: string | null
      previous: string | null
      results: PaymentLink[]
    }
  }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sale_shop) queryParams.append('sale_shop', params.sale_shop.toString())
    if (params?.is_active !== undefined)
      queryParams.append('is_active', params.is_active.toString())

    const queryString = queryParams.toString()
    const url = queryString ? `/payment-links/?${queryString}` : '/payment-links/'

    return apiClient.get<{
      success: boolean
      message: string
      data: {
        count: number
        next: string | null
        previous: string | null
        results: PaymentLink[]
      }
    }>(url)
  }

  // Get payment link by ID
  async getPaymentLink(paymentLinkId: number): Promise<PaymentLink> {
    return apiClient.get<PaymentLink>(`/payment-links/${paymentLinkId}/`)
  }

  // Create payment link
  async createPaymentLink(data: CreatePaymentLinkData): Promise<PaymentLink> {
    return apiClient.post<PaymentLink>('/payment-links/', data)
  }

  // Update payment link
  async updatePaymentLink(
    paymentLinkId: number,
    data: UpdatePaymentLinkData
  ): Promise<PaymentLink> {
    return apiClient.patch<PaymentLink>(`/payment-links/${paymentLinkId}/`, data)
  }

  // Delete payment link
  async deletePaymentLink(paymentLinkId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `/payment-links/${paymentLinkId}/`
    )
  }

  // Handle API errors
  handleError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.errors) {
      return Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : error.response.data.errors
    }
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  }
}

export const dashboardService = new DashboardService()
